import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { AppState, RootPaths } from '@fcom/core';
import { loginStatus, profileType as profileTypeSelector } from '@fcom/core/selectors';
import { LoginStatus, ProfileType } from '@fcom/core-api/login';

import { UpgradeService as ApiUpgradeService } from '../api/services';
import { Buyer, GetUpgradeStatusResponse, UpgradeFlightsV2, UpgradeResponse } from '../api/models';
import { BookingRef, ExtendedFlightBasket, FlightBasket } from '../interfaces/upgrades.interface';
import { UpgradeError } from '../interfaces/upgrade-error.interface';
import {
  getBasketFlights,
  getBasketPassengerIds,
  getBookingRef,
  getUpgradeResponse,
  getUser,
} from '../store/selectors';
import { createPaymentObject } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class UpgradeFlightsService {
  bookingRef$: Observable<BookingRef>;
  upgradeResponse$: Observable<UpgradeResponse>;
  selectedPassengers$: Observable<string[]>;
  selectedFlights$: Observable<FlightBasket[]>;
  user$: Observable<Buyer>;

  private get loggedIn$(): Observable<boolean> {
    return this.store$.pipe(
      loginStatus(),
      map((status) => status === LoginStatus.LOGGED_IN)
    );
  }

  private get profileType$(): Observable<ProfileType> {
    return this.store$.pipe(profileTypeSelector());
  }

  constructor(
    private store$: Store<AppState>,
    private apiUpgradeService: ApiUpgradeService,
    private languageService: LanguageService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.bookingRef$ = this.store$.pipe(select(getBookingRef));
    this.upgradeResponse$ = this.store$.pipe(select(getUpgradeResponse));
    this.selectedPassengers$ = this.store$.pipe(select(getBasketPassengerIds));
    this.selectedFlights$ = this.store$.pipe(select(getBasketFlights));
    this.user$ = this.store$.pipe(select(getUser));
  }

  upgradeFlights(): Observable<UpgradeResponse> {
    return combineLatest([
      this.selectedFlights$,
      this.user$,
      this.selectedPassengers$,
      this.bookingRef$,
      this.loggedIn$,
      this.profileType$,
    ]).pipe(
      first(),
      switchMap(
        ([selectedFlights, user, passengers, bookingRef, loggedIn, profileType]: [
          ExtendedFlightBasket[],
          Buyer,
          string[],
          BookingRef,
          boolean,
          ProfileType,
        ]) => {
          const upgradeUrl = `${this.document.location.origin}/${this.languageService.langValue}/${RootPaths.ONE_UPGRADE_ROOT}`;
          const params = {
            ...bookingRef,
            body: {
              email: user?.email || '',
              flights: null,
              locale: this.languageService.langValue,
              paymentFailUrl: `${upgradeUrl}/payment-error`,
              paymentSuccessUrl: `${upgradeUrl}/success`,
            },
          };

          params.body.flights = this.getFlightsToUpgrade(selectedFlights, passengers);

          return loggedIn && profileType === ProfileType.FPLUS
            ? this.apiUpgradeService.upgradeV2Authenticated(params)
            : this.apiUpgradeService.upgradeV2(params);
        }
      ),
      map((response: UpgradeResponse) => {
        if (!response.url || !response.upgradeId) {
          throw new Error(UpgradeError.paymentError);
        }

        return response;
      }),
      catchError(() => throwError(() => UpgradeError.paymentError))
    );
  }

  getUpgradeStatus(): Observable<GetUpgradeStatusResponse> {
    return combineLatest([this.bookingRef$, this.upgradeResponse$]).pipe(
      first(),
      switchMap(([bookingRef, upgradeResponse]: [BookingRef, UpgradeResponse]) => {
        return this.apiUpgradeService.getUpgradeStatus({
          recLoc: bookingRef.recLoc,
          upgradeId: upgradeResponse.upgradeId,
        });
      }),
      map((response: GetUpgradeStatusResponse) => {
        if (!response.status) {
          throw new Error(UpgradeError.paymentError);
        }

        return response;
      }),
      catchError(() => throwError(() => UpgradeError.paymentError))
    );
  }

  private getFlightsToUpgrade(flightsBasket: ExtendedFlightBasket[], passengers: string[]): UpgradeFlightsV2 {
    return passengers
      .map((passengerId) => {
        return flightsBasket.map((flightBasket) => {
          return {
            flightId: flightBasket.flightId,
            passengerId: passengerId,
            payment: createPaymentObject(flightsBasket, flightBasket),
            productName: flightBasket.productName,
          };
        });
      })
      .reduce((acc, val) => acc.concat(val), []);
  }
}
